export interface SparklinePoint {
  key: string
  label: string
  value: number
}

export interface SparklineProps {
  points: SparklinePoint[]
  valueFormatter?: (value: number) => string
  /** Total/summary label under the headline number, e.g. "across 6 jurisdictions". */
  summaryLabel?: string | undefined
}

/**
 * The `micro`-density fallback for Bar/Line/Donut/Funnel (design-system.md §5 "auto-density" —
 * "collapse to a sparkline or a single summary stat"). A cheap flex/CSS bar strip (no Recharts, no
 * axes, no legend) plus one headline number, so a widget squeezed to a sliver of a mobile screen
 * still shows something meaningful rather than an unreadable miniature full chart. The complete data
 * is never lost — `ChartFrame`'s "view as table" toggle always stays available (§5.3 a11y).
 */
export function Sparkline({ points, valueFormatter = (v) => String(v), summaryLabel }: SparklineProps) {
  if (points.length === 0) {
    return <p className="iw-sparkline__empty">No data for the current filter.</p>
  }
  const max = Math.max(...points.map((p) => Math.abs(p.value)), 0)
  const total = points.reduce((sum, p) => sum + p.value, 0)
  const top = [...points].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))[0]

  return (
    <div className="iw-sparkline">
      <div className="iw-sparkline__bars" role="img" aria-label={`Trend across ${points.length} value${points.length === 1 ? '' : 's'}; use "view as a table" for exact figures.`}>
        {points.map((p) => (
          <span
            key={p.key}
            className="iw-sparkline__bar"
            style={{ height: max > 0 ? `${Math.max(8, (Math.abs(p.value) / max) * 100)}%` : '6%' }}
          />
        ))}
      </div>
      <div className="iw-sparkline__summary">
        <span className="iw-kpi-value">{valueFormatter(total)}</span>
        <span className="iw-sparkline__caption">
          {summaryLabel ?? (top ? `Top: ${top.label}` : '')}
        </span>
      </div>
    </div>
  )
}
